import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { DataService } from 'src/app/services/data.service';
import { ResourceType } from 'src/app/models/ResourceType';
import { answerTypeConverter } from './answer-type-converter';
import { AttachmentService } from './attachment.service';
import { ToastService } from '../error/toast/toast.service';
import { Detail } from '../models/Detail';
import { ExtensionService } from '../services/extension.service';
import cloneDeep from 'lodash/cloneDeep';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireResponseService {
  private readonly dataService = inject(DataService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly toastService = inject(ToastService);
  private readonly extensionService = inject(ExtensionService);


  /**
   * @param items list of QuestionnaireItems / QuestionnaireResponseItems
   * @param linkId linkId of item being searched
   * @param index index of match to return, defaults to 0
   * @returns the index-th occurence of Questionnaire/QuestionnaireResponseItem with the specified linkId
   *
   * This method is for doing a breadth-first search on an array of items that could contain items.
   * It utilizes a queue to store the initial list of items. Every time an item that contains items is encountered,
   * this gets added to the queue. It goes through the queue to look for items that match the linkId.
   * If index is specified, a counter is incremented until the specified number of matches is found, and this value is returned.
   * If no value is found after going through all of the items in the queue, return null.
   */
  findItem(
    items:
      | fhirR4.QuestionnaireItem[]
      | fhirR4.QuestionnaireResponseItem[]
      | undefined,
    linkId: string,
    index?: number,
  ): fhirR4.QuestionnaireItem | fhirR4.QuestionnaireResponseItem | null {
    index = index ?? 0;
    if (items) {
      const queue = [...items];
      let count = 0;

      while (queue.length > 0) {
        const current = queue.shift();

        if (current?.linkId === linkId) {
          if (count === index) {
            return current;
          }
          count++;
        }

        if (current?.item) {
          queue.push(...current.item);
        }
      }
    }

    return null;
  }

  async updateAnswer(
    qItem: fhirR4.QuestionnaireItem,
    detail: Detail,
    qrItem: fhirR4.QuestionnaireResponseItem,
  ): Promise<fhirR4.QuestionnaireResponseItem> {
    const type = qItem.type;
    const newOrigin = this.extensionService.createOriginExtension(
      qrItem.answer,
      qItem.repeats || type === 'attachment',
    );
    const value =
      type === 'attachment'
        ? await this.attachmentService.processAttachments(
            detail,
            qrItem,
            newOrigin,
          )
        : detail.value;
    if (type && type in answerTypeConverter) {
      qrItem.answer = answerTypeConverter[type](
        value,
        newOrigin,
        qItem,
        qrItem,
        detail.code,
      );
      qrItem.extension = [
        {
          url: ExtensionService.authorExtensionUrl,
          valueReference: {
            reference: this.dataService.fhirUser,
          },
        },
      ];
    }
    return qrItem;
  }

  /**
   * Updates the answer value of a specific item in the QR, specified by detail.linkId.
   * async because attachment items require the creation of resources that are then referenced in the answer
   * */
  async updateQR(
    questionnaire: fhirR4.Questionnaire,
    qr: fhirR4.QuestionnaireResponse,
    detail: Detail,
  ): Promise<fhirR4.QuestionnaireResponse> {
    const questionnaireResponse = cloneDeep(qr);
    if (detail.reset) {
      this.resetAnswers(questionnaireResponse, detail.reset);
    } else {
      const qItem = this.findItem(
        questionnaire.item,
        detail.linkId,
      ) as fhirR4.QuestionnaireItem;
      const qrItem = this.findItem(
        questionnaireResponse.item,
        detail.linkId,
        detail.index,
      ) as fhirR4.QuestionnaireResponseItem;
      if (detail.remove && detail.type !== 'attachment') {
        this.deleteItem(qrItem);
      } else if (detail.add) {
        this.addRepeatingGroup(questionnaireResponse, detail, qItem);
      } else {
        await this.updateAnswer(qItem, detail, qrItem);
      }
    }
    return questionnaireResponse;
  }

  async saveQR(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    detail?: Detail,
  ) {
    const retVal = (await this.dataService.updateResource(
      questionnaireResponse,
    )) as fhirR4.QuestionnaireResponse;

    if (detail?.remove && detail.type === 'attachment')
      this.dataService.deleteResource(detail.attachment.reference);
    this.toastService.closeNetworkErrorToast();
    return retVal;
  }

  addRepeatingGroup(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    detail: Detail,
    qItem: fhirR4.QuestionnaireItem,
  ) {
    const idx = questionnaireResponse.item?.findIndex(
      (value) => value.linkId === detail.linkId,
    );
    if (typeof idx === 'number' && idx >= 0) {
      questionnaireResponse.item?.splice(idx + detail.index, 0, qItem);
    }
  }

  deleteItem(qrItem: fhirR4.QuestionnaireResponseItem) {
    Object.keys(qrItem).forEach((key) => {
      delete qrItem[key];
    });
  }

  // resetAnswers only matches the first occurrence of the linkId from resetList,
  // so repeating groups and items are not currently supported
  resetAnswers(questionnaireResponse: fhirR4.QuestionnaireResponse, resetList) {
    resetList
      .flatMap((item) => {
        const qrItem = this.findItem(
          questionnaireResponse.item,
          item.linkId,
        ) as fhirR4.QuestionnaireResponseItem;
        if (item.type === 'group') {
          return this.expandGroup(qrItem);
        } else {
          return qrItem;
        }
      })
      .map((item) => this.deleteAnswerAndAuthor(item));
  }

  private expandGroup(group: fhirR4.QuestionnaireResponseItem) {
    if (!group.item) {
      return group;
    }
    return group.item.flatMap((item) => this.expandGroup(item));
  }

  deleteAnswerAndAuthor(qrItem: fhirR4.QuestionnaireResponseItem) {
    delete qrItem.answer;
    qrItem.extension = qrItem.extension?.filter(
      (ext) => ext.url !== ExtensionService.authorExtensionUrl,
    );
  }

  async saveQrTransaction(
    questionnaire: fhirR4.Questionnaire,
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    detail: Detail,
  ) {
    const qr = await this.updateQR(
      questionnaire,
      questionnaireResponse,
      detail,
    );
    const bundle = this.createTransactionBundle(qr, detail);
    await this.dataService.post(bundle);
    const retVal = (await this.dataService.getResource(
      `${questionnaireResponse.resourceType}/${questionnaireResponse.id}`,
    )) as fhirR4.QuestionnaireResponse;
    this.toastService.closeNetworkErrorToast();
    return retVal;
  }

  createTransactionBundle(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    detail: Detail,
  ) {
    const qrEntry = this.createQRUpdateEntry(questionnaireResponse);

    const docRefEntry = this.createDeleteBundleEntry(detail);

    const bundle = new fhirR4.Bundle();
    bundle.resourceType = ResourceType.Bundle;
    bundle.type = 'transaction';

    bundle.entry = [qrEntry, docRefEntry];
    return bundle;
  }

  private createDeleteBundleEntry(detail: Detail) {
    const docRefEntry = new fhirR4.BundleEntry();
    docRefEntry.request = new fhirR4.BundleRequest();
    docRefEntry.request.method = 'DELETE';
    docRefEntry.request.url = detail.attachment.url;
    return docRefEntry;
  }

  private createQRUpdateEntry(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
  ) {
    const qrEntry = new fhirR4.BundleEntry();
    qrEntry.resource = questionnaireResponse;
    qrEntry.request = new fhirR4.BundleRequest();
    qrEntry.request.method = 'PUT';
    qrEntry.request.url = `${questionnaireResponse.resourceType}/${questionnaireResponse.id}`;
    return qrEntry;
  }
}
